import { Call, Card as CardId, Seat } from "@ravalmatic/bridge-engine/core";
import { HandMode } from "../../../Types";
import { PlayerHand } from "../PlayerHand";
import { Avatar, AvatarExpression } from "./Avatar";
import { BiddingHistoryContainer } from "./bidding-history/BiddingHistoryContainer";
import { PlayerLayout } from "../BoardLayoutManager";
import { CardPresentation } from "../Card";
import { CardinalContainer } from "./CardinalContainer";
import type { PlayerViewState } from "../BoardViewState";
import { getPlayerIdentity, isLocalPlayerSeat } from "../PlayerSeats";
import { GameVars } from "../../../GameVars";

function cardsMatch(first: readonly CardId[], second: readonly CardId[]): boolean {

    return first.length === second.length && first.every((card, index) => card === second[index]);
}

export class BasePlayerContainer extends Phaser.GameObjects.Container {

    public readonly seat: Seat;

    protected readonly hand: PlayerHand;
    protected avatar: Avatar;
    protected readonly cardinalContainer: CardinalContainer;
    protected biddingHistory?: BiddingHistoryContainer;
    private turnActive = false;
    private turnPulsing = false;
    private thinking = false;
    private contractLost = false;
    private tricksWon = 0;
    private previousTrickCard: CardId | null = null;
    private renderedLegalCards?: readonly CardId[];
    private handLayoutPosition?: { x: number; y: number };
    private avatarLayoutPosition?: { x: number; y: number };
    private cardinalOffsetX: number;
    private readonly cardinalOffsetY: number;
    private playCardinal = false;

    constructor(scene: Phaser.Scene, seat: Seat, cards: readonly CardId[], handMode = HandMode.HIDDEN) {

        super(scene);

        this.seat = seat;
        const identity = getPlayerIdentity(seat, GameVars.gameData?.playerAvatarId);
        const localPlayer = isLocalPlayerSeat(seat);
        this.biddingHistory = new BiddingHistoryContainer(scene, seat);
        this.avatar = new Avatar(scene, identity.avatarId, !localPlayer, localPlayer ? 1 : 0, identity.profileId);
        this.cardinalContainer = new CardinalContainer(scene, seat);
        this.cardinalOffsetX = localPlayer ? 90 : 0;
        const nameBottom = this.avatar.nameLabel
            ? this.avatar.nameLabel.y + this.avatar.nameLabel.height : 65;
        this.cardinalOffsetY = localPlayer ? 0 : nameBottom + 26;
        this.cardinalContainer.setPosition(this.cardinalOffsetX, this.cardinalOffsetY);

        this.hand = new PlayerHand(scene, seat, cards, handMode);
        
        this.add([this.biddingHistory, this.avatar, this.cardinalContainer, this.hand]);
        this.relayout();
    }

    public relayout(): void {

        this.setScale(1, GameVars.scaleY);
        if (isLocalPlayerSeat(this.seat)) {
            this.cardinalOffsetX = (GameVars.isLandscape ? 1 : -1)
                * (90 + (this.playCardinal ? 29 * this.cardinalContainer.scaleX : 0));
            this.cardinalContainer.setPosition(
                this.avatar.x + this.cardinalOffsetX, this.avatar.y + this.cardinalOffsetY
            );
        }
    }

    public render(viewState: PlayerViewState | null): void {

        if (viewState === null) {
            if (this.hand.isPlayEnabled()) {
                this.setPlayEnabled(false);
            }
            this.renderedLegalCards = undefined;
            this.setPlayTurnActive(false);
            this.setLastCard(null);
            return;
        }
        if (viewState.seat !== this.seat) {
            throw new Error(`Cannot render seat ${viewState.seat} in player seat ${this.seat}`);
        }
        this.setTricksWon(viewState.tricksWon);
        this.setLastCard(viewState.previousTrickCard);
        this.setAvatarState(viewState.thinking, viewState.contractLost);
        this.setTurnActive(viewState.turnActive, true);
        if (this.hand.mode !== viewState.handMode
            || this.hand.isPlayEnabled() !== (viewState.handMode === HandMode.INTERACTIVE)
            || !cardsMatch(this.hand.cards, viewState.cards)
            || !this.renderedLegalCards || !cardsMatch(this.renderedLegalCards, viewState.legalCards)) {
            this.updatePlayHand(viewState.cards, viewState.handMode, viewState.legalCards);
        }
    }

    public setHandAngle(angle: number): this {

        this.hand.setAngle(angle);
        return this;
    }

    public applyLayout(layout: PlayerLayout): void {

        this.setPosition(layout.position.x, layout.position.y);
        this.hand.setAngle(layout.hand.angle);
        if (this.handLayoutPosition?.x !== layout.hand.x || this.handLayoutPosition.y !== layout.hand.y) {
            const animateHand = this.handLayoutPosition !== undefined && (layout.hand.duration ?? 0) > 0;
            this.handLayoutPosition = { x: layout.hand.x, y: layout.hand.y };
            this.scene.tweens.killTweensOf(this.hand);
            if (animateHand) {
                this.scene.tweens.add({
                    targets: this.hand,
                    x: layout.hand.x,
                    y: layout.hand.y,
                    duration: layout.hand.duration,
                    ease: "Cubic.Out",
                });
            } else {
                this.hand.setPosition(layout.hand.x, layout.hand.y);
            }
        }
        const { x, y, duration = 0 } = layout.avatar;
        if (this.avatarLayoutPosition?.x === x && this.avatarLayoutPosition.y === y) {
            return;
        }
        const animate = this.avatarLayoutPosition !== undefined && duration > 0;
        this.avatarLayoutPosition = { x, y };
        this.positionAvatar(x, y, animate ? duration : 0);
    }

    protected positionAvatar(x: number, y: number, duration = 0, onComplete?: () => void): void {

        const positions = [
            { target: this.avatar, x, y },
            { target: this.cardinalContainer, x: x + this.cardinalOffsetX, y: y + this.cardinalOffsetY }
        ];
        positions.forEach(position => {
            this.scene.tweens.killTweensOf(position.target);
            if (duration > 0) {
                this.scene.tweens.add({
                    targets: position.target,
                    x: position.x,
                    y: position.y,
                    duration,
                    ease: "Cubic.Out",
                    onComplete: position.target === this.cardinalContainer ? onComplete : undefined
                });
                if (duration === 0) {
                    onComplete?.();
                }
            } else {
                position.target.setPosition(position.x, position.y);
            }
        });
    }

    public showPlayCardinal(): void {

        this.cardinalContainer.showPlay();
        this.tricksWon = 0;
        this.playCardinal = true;
        this.relayout();
    }

    public setTricksWon(tricks: number): void {

        if (this.tricksWon === tricks) {
            return;
        }
        this.tricksWon = tricks;
        this.cardinalContainer.setTricksWon(tricks);
    }

    public setLastCard(card: CardId | null): void {

        if (this.previousTrickCard === card) {
            return;
        }
        this.previousTrickCard = card;
        this.cardinalContainer.setLastCard(card);
    }

    public setHandVisible(visible: boolean): void {

        this.hand.setVisible(visible);
    }

    public stopHandAnimations(): void {

        this.hand.stopAnimations();
    }

    public setBiddingHistory(calls: readonly Call[]): void {

        this.biddingHistory?.setCalls(calls);
    }

    public setBiddingTurnActive(active: boolean): void {

        this.biddingHistory?.setTurnActive(active);
        this.setAvatarState(active);
        this.setTurnActive(active);
    }

    public setPlayTurnActive(active: boolean, thinking = false): void {

        this.setAvatarState(active && thinking);
        this.setTurnActive(active, true);
    }

    public setContractLost(lost: boolean): void {

        this.setAvatarState(this.thinking, lost);
    }

    private setAvatarState(thinking: boolean, contractLost = this.contractLost): void {

        const localPlayer = isLocalPlayerSeat(this.seat);
        thinking = thinking && !localPlayer;
        contractLost = contractLost && !localPlayer;
        if (this.thinking === thinking && this.contractLost === contractLost) {
            return;
        }
        this.thinking = thinking;
        this.contractLost = contractLost;
        this.avatar.setExpression(this.thinking ? AvatarExpression.THINKING
            : this.contractLost ? AvatarExpression.TIRED : AvatarExpression.NORMAL);
    }

    public get handMode(): HandMode {

        return this.hand.mode;
    }

    public getCardPresentation(
        card: CardId, relativeTo?: Phaser.GameObjects.Container
    ): CardPresentation | undefined {

        return this.hand.getCardPresentation(card, relativeTo);
    }

    private setTurnActive(active: boolean, pulsing = false): void {

        const turnPulsing = active && pulsing;
        if (this.turnActive !== active || this.turnPulsing !== turnPulsing) {
            this.turnActive = active;
            this.turnPulsing = turnPulsing;
            if (active) {
                this.activate(turnPulsing);
            } else {
                this.deactivate();
            }
        }
    }

    public setPlayEnabled(enabled: boolean): void {

        this.renderedLegalCards = undefined;
        this.hand.setPlayEnabled(enabled);
    }

    public setPlayFeedbackEnabled(enabled: boolean): void {

        this.hand.setPlayFeedbackEnabled(enabled);
    }

    public setCardSelectedHandler(handler: (seat: Seat, card: CardId) => void): void {

        this.hand.setCardSelectedHandler(handler);
    }

    public updatePlayHand(cards: readonly CardId[], mode: HandMode, legalCards: readonly CardId[]): void {

        this.hand.updatePlayHand(cards, mode, legalCards);
        this.renderedLegalCards = [...legalCards];
    }

    public hideBiddingHistory(onComplete: () => void): void {

        if (!this.biddingHistory) {
            onComplete();
            return;
        }
        this.biddingHistory.hideAnimated(() => {
            this.biddingHistory = undefined;
            onComplete();
        });
    }

    public destroy(fromScene?: boolean): void {

        this.scene.tweens.killTweensOf(this.hand);
        this.scene.tweens.killTweensOf(this.avatar);
        this.scene.tweens.killTweensOf(this.cardinalContainer);
        super.destroy(fromScene);
    }

    public activate(pulsing = false): void {

        this.avatar.activate(pulsing);
    }

    public deactivate(): void {

        this.avatar.deactivate();
    }
}
